import { captureTransaction, refundTransaction, cancelTransaction } from 'common/api/frontend';
import { TransactionApi, RefundRequest, PaymentApi, CaptureRequest, Languages } from 'common/types';
import { AmountObject } from 'common/types/adyen';

export interface CapturePaymentProps {
	amount: AmountObject;
	captureDescription: string;
	shopMessage: string;
	userEmail: string;
	language: Languages | undefined;
}

export const capturePayment = async (transaction: TransactionApi, data: CapturePaymentProps) => {
	const { amount, captureDescription, shopMessage, userEmail, language } = data;
	const captureData: CaptureRequest = {
		originalTransactionId: transaction.id,
		amount,
		channel: 'ADMIN',
		method: transaction.method,
		captureDescription,
		shopMessage,
		userEmail,
		language,
	};
	await captureTransaction(captureData);
};

export const refundPayment = async (transaction: TransactionApi, amount: AmountObject) => {
	const refundData: RefundRequest = {
		originalTransactionId: transaction.id,
		amount,
		channel: 'ADMIN',
		method: transaction.method,
	};
	await refundTransaction(refundData);
};

export const cancelPayment = async (transaction: TransactionApi) => {
	const refundData: RefundRequest = {
		originalTransactionId: transaction.id,
		channel: 'ADMIN',
		method: transaction.method,
	};
	await cancelTransaction(refundData);
};

export const sortDepositFirst = (a: PaymentApi, b: PaymentApi) => {
	if (a.paymentType === 'DEPOSIT' && b.paymentType !== 'DEPOSIT') {
		return -1;
	}
	if (b.paymentType === 'DEPOSIT' && a.paymentType !== 'DEPOSIT') {
		return 1;
	}
	return 0;
};

export interface PaymentWithTransactions {
	payment: PaymentApi;
	transactions: TransactionApi[];
}

export interface PaymentWithModifications {
	original: PaymentWithTransactions;
	modifications: PaymentWithTransactions[];
}

const getModificationPayments = (payment: PaymentApi, params: PaymentsWithModificationsRequest) => {
	const { payments, transactions } = params;
	const paymentModifications = payments
		.filter((p) => p.originalPaymentId === payment.id)
		.map((p) => ({
			payment: p,
			transactions: transactions[p.id] || [],
		}));
	return paymentModifications;
};

interface PaymentsWithModificationsRequest {
	payments: PaymentApi[];
	transactions: { [paymentId: string]: TransactionApi[] };
}

// Original payments = payments that are not modification payments, such as refund / capture of existing payment
export const getPaymentsWithModifications = (
	params: PaymentsWithModificationsRequest,
): PaymentWithModifications[] => {
	const { payments, transactions } = params;
	const originalPayments = payments.filter((p) => !p.originalPaymentId);
	return originalPayments.map((payment) => {
		const modificationPayments = getModificationPayments(payment, params);
		const originalPayment: PaymentWithTransactions = {
			payment,
			transactions: transactions[payment.id] || [],
		};
		return {
			original: originalPayment,
			modifications: modificationPayments,
		};
	});
};
